/**
 * mock.ts — deterministic Courtage fixtures (Story UI-U5) mirroring the wire DTOs.
 *
 * The mock layer is the dev/test default (getDataMode() — the V10-J pattern); it is
 * NOT deleted when live wiring lands. Covers: one header_exception statement, an
 * "automatisch abgeglichen" statement (braucht_entscheid=false), one Korrekturgruppe,
 * one Akonto line (kind '06'), one override-decided line, and a Finance-gated variant
 * (all CHF fields null → the `—` render test).
 *
 * [Source: apps/web/src/features/invoices/mock.ts] (fixture-module pattern)
 * [Source: docs/stories/ui-u5-courtage-tarifmatrix.md#Tasks — Task 5]
 */

import type {
  AkontoBalanceViewDto,
  CommissionExpectationViewDto,
  CommissionLineViewDto,
  CommissionSplitViewDto,
  CommissionStatementDetailDto,
  CommissionStatementListItemDto,
  ReconciliationKennzahlenDto,
} from '@brokly/shared';

// ── Statement list ──────────────────────────────────────────────────────────

export const COURTAGE_STATEMENTS_MOCK: CommissionStatementListItemDto[] = [
  {
    statement_id: 'stmt-axa-01',
    carrier_uid: 'CHE-105.805.144',
    carrier_name: 'AXA',
    period_from: '2026-01-01',
    period_to: '2026-01-31',
    currency: 'CHF',
    status: 'header_ok',
    source_type: 'carrier',
    line_count: 4,
    open_line_count: 2,
    braucht_entscheid: true,
    soll_total: '12500.00',
    ist_total: '12180.00',
    delta_total: '-320.00',
    check_score: '0.9200',
    created_at: '2026-02-03T08:00:00.000Z',
  },
  {
    statement_id: 'stmt-swisslife-02',
    carrier_uid: 'CHE-105.940.833',
    carrier_name: 'Swiss Life',
    period_from: '2026-01-01',
    period_to: '2026-01-31',
    currency: 'CHF',
    status: 'header_exception',
    source_type: 'carrier',
    line_count: 3,
    open_line_count: 0,
    braucht_entscheid: true,
    soll_total: '8000.00',
    ist_total: '8000.00',
    delta_total: '0.00',
    check_score: '0.4000',
    created_at: '2026-02-02T09:30:00.000Z',
  },
  {
    statement_id: 'stmt-helvetia-03',
    carrier_uid: 'CHE-106.982.581',
    carrier_name: 'Helvetia',
    period_from: '2025-12-01',
    period_to: '2025-12-31',
    currency: 'CHF',
    status: 'header_ok',
    source_type: 'carrier',
    line_count: 5,
    open_line_count: 0,
    braucht_entscheid: false,
    soll_total: '9400.00',
    ist_total: '9400.00',
    delta_total: '0.00',
    check_score: '0.9900',
    created_at: '2026-01-05T07:15:00.000Z',
  },
  {
    // Finance-gated variant: all CHF fields null → the peek/list render `—`.
    statement_id: 'stmt-gated-04',
    carrier_uid: 'CHE-107.111.222',
    carrier_name: 'Mobiliar',
    period_from: '2026-01-01',
    period_to: '2026-01-31',
    currency: 'CHF',
    status: 'header_ok',
    source_type: 'carrier',
    line_count: 2,
    open_line_count: 1,
    braucht_entscheid: true,
    soll_total: null,
    ist_total: null,
    delta_total: null,
    check_score: '0.8800',
    created_at: '2026-02-04T10:00:00.000Z',
  },
];

// ── Statement details (keyed by statement_id) ────────────────────────────────

function baseLine(over: Partial<CommissionLineViewDto>): CommissionLineViewDto {
  return {
    line_id: 'line-x',
    policy_id: 'pol-x',
    policy_number: 'POL-000',
    policy_number_raw: 'POL000',
    kunde_name: 'Muster AG',
    product_label: 'BVG Sammelstiftung',
    commission_kind: '01',
    settlement_type: '01',
    line_period_from: '2026-01-01',
    line_period_to: '2026-01-31',
    ist: '3000.00',
    soll: '3000.00',
    delta_chf: '0.00',
    delta_pct: '0.0000',
    direction: 'none',
    match_level: 'L1',
    diff_class: null,
    line_status: 'matched',
    soll_status: 'computed',
    correction_group_id: null,
    is_akonto: false,
    check_status: 'matched',
    match_stage: 'exact',
    mandate_valid: true,
    policy_active: true,
    broker_assigned: true,
    approved_tariff_exists: true,
    bemessung: '300000.00',
    applied_rate: '0.010000',
    applied_factor: '1.000000',
    policy_version_id: 'pv-1',
    tariff_id: 'tar-1',
    override_reason: null,
    override_actor_kind: null,
    created_at: '2026-02-03T08:00:00.000Z',
    ...over,
  };
}

const AXA_DETAIL: CommissionStatementDetailDto = {
  ...COURTAGE_STATEMENTS_MOCK[0]!,
  broker_vu_code: 'VU-4711',
  broker_finma_nr: 'F01234',
  total_amount: '12180.00',
  sum_lines_ist: '12180.00',
  kontrollsumme_ok: true,
  lines: [
    baseLine({
      line_id: 'line-axa-1',
      policy_number: 'AXA-88120',
      kunde_name: 'Bergland Logistik AG',
      product_label: 'BVG Sammelstiftung',
      ist: '2680.00',
      soll: '3000.00',
      delta_chf: '-320.00',
      delta_pct: '-10.6700',
      direction: 'underpayment',
      match_level: 'L3',
      diff_class: 'underpayment',
      line_status: 'deviation_open',
    }),
    baseLine({
      line_id: 'line-axa-2',
      policy_number: 'AXA-88121',
      kunde_name: 'Muster Handel GmbH',
      commission_kind: '06',
      is_akonto: true,
      product_label: 'UVG',
      soll: null,
      delta_chf: null,
      delta_pct: null,
      direction: null,
      match_level: 'L1',
      diff_class: null,
      line_status: 'pending',
      soll_status: 'akonto',
    }),
    baseLine({
      line_id: 'line-axa-3',
      policy_number: 'AXA-88122',
      kunde_name: 'Weber Immobilien AG',
      ist: '3200.00',
      soll: '3000.00',
      delta_chf: '200.00',
      delta_pct: '6.6700',
      direction: 'overpayment',
      match_level: 'L3',
      diff_class: 'overpayment',
      line_status: 'resolved',
      override_reason: 'Nachträgliche Korrektur mit dem Versicherer geklärt und akzeptiert.',
      override_actor_kind: 'human',
    }),
    baseLine({
      line_id: 'line-axa-4',
      policy_number: 'AXA-88123',
      kunde_name: 'Bergland Logistik AG',
      ist: '-2900.00',
      soll: '-3000.00',
      delta_chf: '100.00',
      delta_pct: '3.3300',
      direction: 'overpayment',
      match_level: 'L2',
      diff_class: null,
      line_status: 'matched',
      correction_group_id: 'grp-axa-1',
      soll_status: 'correction_group_net',
    }),
  ],
  correction_groups: [
    {
      id: 'grp-axa-1',
      policy_id: 'pol-x',
      net_amount: '0.00',
      net_expected: '0.00',
      is_clean_correction: true,
      member_count: 2,
    },
  ],
};

const SWISSLIFE_DETAIL: CommissionStatementDetailDto = {
  ...COURTAGE_STATEMENTS_MOCK[1]!,
  broker_vu_code: 'VU-9000',
  broker_finma_nr: 'F09999',
  total_amount: '8000.00',
  sum_lines_ist: '7600.00',
  kontrollsumme_ok: false,
  lines: [
    baseLine({
      line_id: 'line-sl-1',
      policy_number: 'SL-22001',
      kunde_name: 'Alpina Treuhand AG',
      ist: '4000.00',
      soll: '4000.00',
      line_status: 'matched',
    }),
    baseLine({
      line_id: 'line-sl-2',
      policy_number: 'SL-22002',
      kunde_name: 'Alpina Treuhand AG',
      product_label: 'Unbekanntes Produkt',
      ist: '3600.00',
      soll: null,
      delta_chf: null,
      delta_pct: null,
      match_level: 'L4',
      diff_class: 'strukturbruch',
      line_status: 'deviation_open',
      soll_status: 'no_tariff',
      check_status: 'strukturbruch',
      approved_tariff_exists: false,
    }),
  ],
  correction_groups: [],
};

const HELVETIA_DETAIL: CommissionStatementDetailDto = {
  ...COURTAGE_STATEMENTS_MOCK[2]!,
  broker_vu_code: 'VU-3300',
  broker_finma_nr: 'F03300',
  total_amount: '9400.00',
  sum_lines_ist: '9400.00',
  kontrollsumme_ok: true,
  lines: [
    baseLine({ line_id: 'line-hv-1', policy_number: 'HV-1', kunde_name: 'Sonne AG', line_status: 'matched' }),
  ],
  correction_groups: [],
};

const GATED_DETAIL: CommissionStatementDetailDto = {
  ...COURTAGE_STATEMENTS_MOCK[3]!,
  broker_vu_code: 'VU-7',
  broker_finma_nr: 'F07',
  total_amount: null,
  sum_lines_ist: null,
  kontrollsumme_ok: true,
  lines: [
    baseLine({
      line_id: 'line-gated-1',
      policy_number: 'MOB-1',
      kunde_name: 'Nebel GmbH',
      ist: null,
      soll: null,
      delta_chf: null,
      delta_pct: null,
      bemessung: null,
      applied_rate: null,
      applied_factor: null,
      match_level: 'L3',
      diff_class: 'underpayment',
      line_status: 'deviation_open',
    }),
  ],
  correction_groups: [],
};

export const COURTAGE_STATEMENT_DETAILS_MOCK: Record<string, CommissionStatementDetailDto> = {
  'stmt-axa-01': AXA_DETAIL,
  'stmt-swisslife-02': SWISSLIFE_DETAIL,
  'stmt-helvetia-03': HELVETIA_DETAIL,
  'stmt-gated-04': GATED_DETAIL,
};

// ── Akonto balances ──────────────────────────────────────────────────────────

export const COURTAGE_AKONTO_MOCK: AkontoBalanceViewDto[] = [
  {
    id: 'ak-1',
    policy_id: 'pol-x',
    policy_number: 'AXA-88121',
    kunde_name: 'Muster Handel GmbH',
    agreement_id: null,
    advanced_amount: '5000.00',
    settled_amount: '2000.00',
    open_balance: '3000.00',
    status: 'offen',
    due_date: '2026-03-31',
    updated_at: '2026-02-03T08:00:00.000Z',
  },
  {
    id: 'ak-2',
    policy_id: 'pol-y',
    policy_number: 'SL-22001',
    kunde_name: 'Alpina Treuhand AG',
    agreement_id: null,
    advanced_amount: '4000.00',
    settled_amount: '1000.00',
    open_balance: '3000.00',
    status: 'ueberfaellig',
    due_date: '2026-01-15',
    updated_at: '2026-02-01T08:00:00.000Z',
  },
];

// ── Co-broker splits ─────────────────────────────────────────────────────────

export const COURTAGE_SPLITS_MOCK: CommissionSplitViewDto[] = [
  {
    id: 'sp-1',
    role: 'lead',
    counterparty_broker_id: 'brk-partner-01',
    commission_kind: '01',
    basis_amount: '10000.00',
    expected_share: '4000.00',
    actual_share: '3800.00',
    delta_chf: '-200.00',
    direction: 'underpayment',
    split_class: 'recipient_share_under',
    match_level: 'L3',
    status: 'payout_due',
    transparency_gap: false,
    payout_ref: null,
    created_at: '2026-02-03T08:00:00.000Z',
  },
  {
    id: 'sp-2',
    role: 'recipient',
    counterparty_broker_id: 'brk-lead-09',
    commission_kind: '01',
    basis_amount: '6000.00',
    expected_share: '2400.00',
    actual_share: '2400.00',
    delta_chf: '0.00',
    direction: 'none',
    split_class: null,
    match_level: 'L1',
    status: 'paid',
    transparency_gap: true,
    payout_ref: 'PAY-2026-014',
    created_at: '2026-02-02T08:00:00.000Z',
  },
];

// ── Reconciliation Kennzahlen ────────────────────────────────────────────────

export const COURTAGE_KENNZAHLEN_MOCK: ReconciliationKennzahlenDto = {
  period_from: '2026-01-01',
  period_to: '2026-01-31',
  ausstehende_courtage_chf: '18500.00',
  offene_differenzen_chf: '520.00',
  offene_differenzen_count: 3,
  offene_akonti_chf: '6000.00',
  reconciliation_quote_pct: 87.5,
  ueberfaellige_erwartungen: 2,
  offene_cobroker_verbindlichkeiten_chf: '200.00',
  offene_cobroker_forderungen_chf: '0.00',
  anteilsabweichungen_count: 1,
  anteilsabweichungen_chf: '200.00',
  nicht_weitergeleitete_splits_chf: '0.00',
};

// ── Expectations ─────────────────────────────────────────────────────────────

export const COURTAGE_EXPECTATIONS_MOCK: CommissionExpectationViewDto[] = [
  {
    id: 'exp-1',
    policy_id: 'pol-x',
    policy_number: 'AXA-88120',
    period_from: '2026-01-01',
    period_to: '2026-01-31',
    commission_kind: '01',
    expected_amount: '3000.00',
    status: 'overdue',
    due_date: '2026-01-20',
  },
  {
    id: 'exp-2',
    policy_id: 'pol-z',
    policy_number: 'HV-1',
    period_from: '2026-01-01',
    period_to: '2026-01-31',
    commission_kind: '01',
    expected_amount: '1800.00',
    status: 'received',
    due_date: '2026-01-25',
  },
];
